import {
  AddPhotoAlternateOutlined,
  ArrowBackOutlined,
  Close as CloseIcon,
  DeleteOutlined,
  EditOutlined,
  ImageOutlined,
  Inventory2Outlined,
  LocationOnOutlined,
  ViewListOutlined,
  ViewModuleOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Card,
  CardMedia,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../components/ui/Button";
import apiService from "../../service/Api";
import { tokens } from "../../theme";
import type {
  AddEquipmentFormData,
  IBrandModel,
  ICategory,
  IEquipment,
  IEquipmentAllocation,
  PaginatedResponse,
} from "../../types/equipment";
import AddEquipmentDialog from "./components/AddEquipmentDialog";
import AddSetDialog from "./components/AddSetDialog";
import AnimatedTable, { ColumnDef } from "./components/AnimatedTable";

const ROWS_PER_PAGE = 12;

const initialAddEquipmentFormData: AddEquipmentFormData = {
  brandModel: {
    brand_model_id: "",
    brand: "",
    model: "",
    category_id: "",
    manufacturer: "",
    description: "",
    length: "",
    height: "",
    width: "",
    weight: "",
    measuring_unit: "cm",
    power_draw: "",
    current_draw: "",
    rental_value: "",
  },
  unit: {
    serial_number: "",
    asset_tag: "",
    status: "available",
    condition: "good",
    location: "",
    description: "",
    purchase_date: "",
    warranty_expiry_date: "",
    purchase_cost: "",
    current_value: "",
    supplier_name: "",
    contact_name: "",
    supplier_phone: "",
    supplier_email: "",
    supplier_website: "",
  },
};

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  available: { label: "Available", color: "#4caf50" },
  allocated: { label: "Allocated", color: "#2196f3" },
  rented: { label: "Rented", color: "#ff9800" },
  maintenance: { label: "Maintenance", color: "#f44336" },
  lost: { label: "Lost", color: "#9e9e9e" },
};

interface RawEquipmentItem {
  id: number | string;
  serial_number?: string;
  gear_name?: string;
  transport_volume?: string | null;
  brand_model?: IBrandModel;
  brand_model_id?: number | string;
  status?: string;
  condition?: string;
  location?: string;
  supplier_name?: string;
  contact_name?: string;
  supplier_phone?: string;
  supplier_email?: string;
  supplier_website?: string;
  purchase_date?: string;
  purchase_cost?: string;
  current_value?: string;
  warranty_expiry_date?: string;
  last_used_date?: string;
  last_maintenance_date?: string;
  next_maintenance_date?: string;
  hours_since_last_service?: number;
  asset_tag?: string;
  images?: Array<{
    id?: number;
    image?: string;
    caption?: string;
  }>;
  allocations?: IEquipmentAllocation[];
}

type EquipmentListResponse =
  | RawEquipmentItem[]
  | PaginatedResponse<RawEquipmentItem>;
type CategoryListResponse = ICategory[] | PaginatedResponse<ICategory>;

function mapRawToEquipment(item: RawEquipmentItem): IEquipment {
  const bm = item.brand_model;
  return {
    id: item.id.toString(),
    serial_number: item.serial_number || "",
    gear_name: item.gear_name || bm?.model || "",
    brand_model: bm,
    brand_model_id: bm?.id,
    status: item.status || "available",
    condition: item.condition || "",
    location: item.location || "",
    supplier_name: item.supplier_name || "",
    contact_name: item.contact_name || "",
    supplier_phone: item.supplier_phone || "",
    supplier_email: item.supplier_email || "",
    supplier_website: item.supplier_website || "",
    purchase_date: item.purchase_date || "",
    purchase_cost: item.purchase_cost || "",
    current_value: item.current_value || "",
    warranty_expiry_date: item.warranty_expiry_date || "",
    last_used_date: item.last_used_date || "",
    last_maintenance_date: item.last_maintenance_date || "",
    next_maintenance_date: item.next_maintenance_date || "",
    hours_since_last_service: item.hours_since_last_service ?? undefined,
    asset_tag: item.asset_tag || "",
    transport_volume: item.transport_volume ?? null,
    images: item.images || [],
    allocations: item.allocations || [],
  };
}

const ListGear: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState<"table" | "grid">("table");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState<IEquipment | null>(
    null,
  );

  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterLocation, setFilterLocation] = useState("all");

  const [imageUploadOpen, setImageUploadOpen] = useState(false);
  const [equipmentForImage, setEquipmentForImage] = useState<IEquipment | null>(
    null,
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [addSetDialogOpen, setAddSetDialogOpen] = useState(false);

  const [importExportAnchorEl, setImportExportAnchorEl] =
    useState<HTMLElement | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [scanFile, setScanFile] = useState<File | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [aiPrefillData, setAiPrefillData] =
    useState<Partial<AddEquipmentFormData> | null>(null);

  const [equipmentList, setEquipmentList] = useState<IEquipment[]>([]);
  const [categories, setCategories] = useState<ICategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [brandModelList, setBrandModelList] = useState<IBrandModel[]>([]);
  const [deleting, setDeleting] = useState<string | null>(null);

  const fetchBrandModels = useCallback(async () => {
    try {
      const res = await apiService.get<PaginatedResponse<IBrandModel>>(
        "/api/equipments/brand-model/",
      );
      setBrandModelList(res.data.results);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchEquipment = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiService.get<EquipmentListResponse>(
        "/api/equipments/equipment/",
      );
      const data = response.data;

      let raw: RawEquipmentItem[] = [];
      if (Array.isArray(data)) {
        raw = data;
      } else if (data && Array.isArray(data.results)) {
        raw = data.results;
      }
      const grouped = new Map<string, string[]>();
      raw.forEach((item) => {
        const key = item.brand_model?.id?.toString() || "unknown";
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key)!.push(item.id.toString());
      });

      setEquipmentList(
        raw.map((item) => {
          const key = item.brand_model?.id?.toString() || "unknown";
          const group = grouped.get(key) || [];
          const position = group.indexOf(item.id.toString()) + 1;
          return {
            ...mapRawToEquipment(item),
            unit_index: `${position}/${group.length}`,
          };
        }),
      );
    } catch {
      setError("Failed to load equipment. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await apiService.get<CategoryListResponse>(
        "/api/equipments/categories/",
      );
      const data = response.data;
      if (Array.isArray(data)) {
        setCategories(data);
      } else if (data && Array.isArray(data.results)) {
        setCategories(data.results);
      }
    } catch {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    fetchEquipment();
    fetchCategories();
    fetchBrandModels();
  }, [fetchEquipment, fetchCategories, fetchBrandModels]);

  const handleOpenImageUpload = (equipment: IEquipment) => {
    setEquipmentForImage(equipment);
    setSelectedFile(null);
    setImageUploadOpen(true);
  };

  const handleUploadImageSubmit = async () => {
    if (!selectedFile || !equipmentForImage) return;
    setIsUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("image", selectedFile);
      formData.append("equipment", equipmentForImage.id.toString());
      await apiService.post("/api/equipments/equipment-images/", formData);
      setImageUploadOpen(false);
      setSelectedFile(null);
      setEquipmentForImage(null);
      await fetchEquipment();
    } catch {
      setError("Failed to upload image.");
    } finally {
      setIsUploading(false);
    }
  };

  const resolveBrandId = async (
    brandName: string,
  ): Promise<string | undefined> => {
    if (!brandName.trim()) return undefined;
    try {
      const res = await apiService.get<
        PaginatedResponse<{ id: number | string; name: string }>
      >(
        `/api/equipments/brands/?search=${encodeURIComponent(brandName.trim())}`,
      );
      const exact = res.data.results.find(
        (b) => b.name.toLowerCase() === brandName.trim().toLowerCase(),
      );
      if (exact) return exact.id.toString();

      const created = await apiService.post<{
        id: number | string;
        name: string;
      }>("/api/equipments/brands/", { name: brandName.trim() });
      return created.data.id.toString();
    } catch {
      return undefined;
    }
  };

  const handleDeleteEquipment = async (eq: IEquipment, e: React.MouseEvent) => {
    e.stopPropagation();
    if (
      !window.confirm(
        `Delete "${eq.gear_name || eq.serial_number}"? This cannot be undone.`,
      )
    )
      return;
    try {
      setDeleting(eq.id);
      await apiService.delete(`/api/equipments/equipment/${eq.id}/`);
      await fetchEquipment();
    } catch {
      setError("Failed to delete equipment.");
    } finally {
      setDeleting(null);
    }
  };

  const handleAddEquipment = async (data: AddEquipmentFormData) => {
    setError(null);
    try {
      let brandModelId = data.brandModel.brand_model_id;

      if (!brandModelId) {
        const brandId = await resolveBrandId(data.brandModel.brand);

        const existingBrandModel = brandModelList.find(
          (bm) =>
            (bm.brand?.toString() ?? "") === (brandId ?? "") &&
            bm.model.trim().toLowerCase() ===
              data.brandModel.model.trim().toLowerCase(),
        );

        if (existingBrandModel && !editingEquipment?.brand_model) {
          // Reuse the catalog entry — creating a new one would violate the
          // (company, brand, model) unique constraint on BrandModel.
          brandModelId = existingBrandModel.id.toString();
        } else {
          const bmPayload = {
            ...(brandId ? { brand: brandId } : {}),
            model: data.brandModel.model,
            ...(data.brandModel.category_id
              ? { category_id: data.brandModel.category_id }
              : {}),
            ...(data.brandModel.manufacturer
              ? { manufacturer: data.brandModel.manufacturer }
              : {}),
            ...(data.brandModel.description
              ? { description: data.brandModel.description }
              : {}),
            ...(data.brandModel.length
              ? { length: data.brandModel.length }
              : {}),
            ...(data.brandModel.height
              ? { height: data.brandModel.height }
              : {}),
            ...(data.brandModel.width ? { width: data.brandModel.width } : {}),
            ...(data.brandModel.weight
              ? { weight: data.brandModel.weight }
              : {}),
            measuring_unit: data.brandModel.measuring_unit || "cm",
            ...(data.brandModel.power_draw
              ? { power_draw: data.brandModel.power_draw }
              : {}),
            ...(data.brandModel.current_draw
              ? { current_draw: data.brandModel.current_draw }
              : {}),
            ...(data.brandModel.rental_value
              ? { rental_value: data.brandModel.rental_value }
              : {}),
          };

          if (editingEquipment?.brand_model) {
            const bmId = editingEquipment.brand_model.id;
            await apiService.patch(
              `/api/equipments/brand-model/${bmId}/`,
              bmPayload,
            );
            brandModelId = bmId.toString();
          } else {
            const bmRes = await apiService.post<{ id: number | string }>(
              "/api/equipments/brand-model/",
              bmPayload,
            );
            brandModelId = bmRes.data.id.toString();
            setBrandModelList((prev) => [
              ...prev,
              { id: bmRes.data.id, brand: brandId, model: data.brandModel.model },
            ]);
          }
        }
      }

      const unitPayload = {
        brand_model_id: brandModelId,
        serial_number: data.unit.serial_number,
        asset_tag: data.unit.asset_tag || undefined,
        status: data.unit.status || "available",
        condition: data.unit.condition || "good",
        location: data.unit.location || undefined,
        description: data.unit.description || undefined,
        purchase_date: data.unit.purchase_date || undefined,
        warranty_expiry_date: data.unit.warranty_expiry_date || undefined,
        purchase_cost: data.unit.purchase_cost || undefined,
        current_value: data.unit.current_value || undefined,
        supplier_name: data.unit.supplier_name || undefined,
        contact_name: data.unit.contact_name || undefined,
        supplier_phone: data.unit.supplier_phone || undefined,
        supplier_email: data.unit.supplier_email || undefined,
        supplier_website: data.unit.supplier_website || undefined,
      };

      if (editingEquipment) {
        await apiService.patch(
          `/api/equipments/equipment/${editingEquipment.id}/`,
          unitPayload,
        );
      } else {
        await apiService.post("/api/equipments/equipment/", unitPayload);
      }

      setEditingEquipment(null);
      setAddDialogOpen(false);
      await fetchEquipment();
    } catch (err: unknown) {
      const apiErr = err as { response?: { data?: unknown } };
      const errData = apiErr?.response?.data;

      const flattenErrors = (obj: unknown): string[] => {
        if (typeof obj === "string") return [obj];
        if (Array.isArray(obj)) return obj.flatMap(flattenErrors);
        if (obj && typeof obj === "object") {
          return Object.values(obj as Record<string, unknown>).flatMap(
            flattenErrors,
          );
        }
        return [];
      };

      const messages = errData ? flattenErrors(errData) : [];
      setError(
        messages.length
          ? messages.join(" · ")
          : "Failed to save equipment. Please try again.",
      );
    }
  };

  const CSV_COLUMNS = [
    "gear_name",
    "brand",
    "model",
    "category",
    "serial_number",
    "asset_tag",
    "status",
    "condition",
    "location",
  ] as const;

  const csvEscape = (value: string) => {
    if (/[",\n]/.test(value)) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  };

  const handleExportCSV = () => {
    setImportExportAnchorEl(null);
    const rows = filteredEquipment.map((eq) => [
      eq.gear_name || "",
      eq.brand_model?.brand_name || "",
      eq.brand_model?.model || "",
      (typeof eq.brand_model?.category === "object"
        ? eq.brand_model?.category?.name
        : eq.brand_model?.category) || "",
      eq.serial_number || "",
      eq.asset_tag || "",
      eq.status || "",
      eq.condition || "",
      eq.location || "",
    ]);
    const csv = [CSV_COLUMNS.join(","), ...rows.map((r) => r.map(csvEscape).join(","))].join(
      "\n",
    );
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "gear-export.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  // Minimal CSV parser: handles quoted fields with embedded commas/newlines.
  const parseCSV = (text: string): string[][] => {
    const rows: string[][] = [];
    let row: string[] = [];
    let field = "";
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      if (inQuotes) {
        if (char === '"' && text[i + 1] === '"') {
          field += '"';
          i++;
        } else if (char === '"') {
          inQuotes = false;
        } else {
          field += char;
        }
      } else if (char === '"') {
        inQuotes = true;
      } else if (char === ",") {
        row.push(field);
        field = "";
      } else if (char === "\n" || char === "\r") {
        if (field !== "" || row.length > 0) {
          row.push(field);
          rows.push(row);
        }
        row = [];
        field = "";
        if (char === "\r" && text[i + 1] === "\n") i++;
      } else {
        field += char;
      }
    }
    if (field !== "" || row.length > 0) {
      row.push(field);
      rows.push(row);
    }
    return rows.filter((r) => r.some((c) => c.trim() !== ""));
  };

  const handleImportCSVFile = async (file: File) => {
    setImportExportAnchorEl(null);
    setIsImporting(true);
    setError(null);
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      if (rows.length === 0) return;

      const header = rows[0].map((h) => h.trim().toLowerCase());
      const dataRows = rows.slice(1);

      const colIndex = (name: string) => header.indexOf(name);

      for (const row of dataRows) {
        const get = (name: string) => {
          const idx = colIndex(name);
          return idx >= 0 ? (row[idx] || "").trim() : "";
        };

        const categoryName = get("category");
        const category = categories.find(
          (c) => c.name.toLowerCase() === categoryName.toLowerCase(),
        );

        const data: AddEquipmentFormData = {
          brandModel: {
            ...initialAddEquipmentFormData.brandModel,
            brand: get("brand"),
            model: get("model") || get("gear_name"),
            category_id: category ? category.id.toString() : "",
          },
          unit: {
            ...initialAddEquipmentFormData.unit,
            serial_number: get("serial_number"),
            asset_tag: get("asset_tag"),
            status: get("status") || "available",
            condition: get("condition") || "good",
            location: get("location"),
          },
        };

        if (!data.unit.serial_number) continue;
        await handleAddEquipment(data);
      }
    } catch {
      setError("Failed to import CSV. Please check the file format.");
    } finally {
      setIsImporting(false);
    }
  };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(",")[1] || "");
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleScanSubmit = async () => {
    if (!scanFile) return;
    setIsScanning(true);
    setScanError(null);
    try {
      const apiKey =
        process.env.REACT_APP_GEMINI_API_KEY ||
        process.env.GEMINI_API_KEY ||
        (window as unknown as { GEMINI_API_KEY?: string }).GEMINI_API_KEY;

      if (!apiKey) {
        setScanError(
          "AI scan is currently unavailable because the API key is not configured.",
        );
        return;
      }

      const base64 = await fileToBase64(scanFile);
      const categoryNames = categories.map((c) => c.name).join(", ");

      const prompt = `You are helping identify a piece of equipment/gear from a photo for a funeral streaming company's inventory system.

Look at the image and extract as much of the following as you can determine:
- gear_name: brand + model
- brand: the manufacturer/brand name, if visible or identifiable
- model: the model name/number, if visible or identifiable
- category: pick the closest match from this list of existing categories (or leave blank if none fit): ${categoryNames}
- serial_number: only if clearly visible in the photo, otherwise leave blank
- condition: one of "new", "good", "fair", "poor", "damaged" based on visual condition
- description: a brief 1-sentence description of the item

Respond with ONLY a JSON object with exactly these keys: gear_name, brand, model, category, serial_number, condition, description. Use empty strings for anything you cannot determine.`;

      const response = await axios.post(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`,
        {
          contents: [
            {
              parts: [
                { inline_data: { mime_type: scanFile.type || "image/jpeg", data: base64 } },
                { text: prompt },
              ],
            },
          ],
          generationConfig: { responseMimeType: "application/json" },
        },
      );

      const generated = response.data?.candidates?.[0]?.content?.parts?.[0]?.text;
      if (!generated) {
        setScanError("Failed to analyze the photo. Please try again.");
        return;
      }

      const parsed = JSON.parse(generated) as {
        gear_name?: string;
        brand?: string;
        model?: string;
        category?: string;
        serial_number?: string;
        condition?: string;
        description?: string;
      };

      const category = categories.find(
        (c) => c.name.toLowerCase() === (parsed.category || "").toLowerCase(),
      );

      setAiPrefillData({
        brandModel: {
          ...initialAddEquipmentFormData.brandModel,
          brand: parsed.brand || "",
          model: parsed.model || parsed.gear_name || "",
          category_id: category ? category.id.toString() : "",
          description: parsed.description || "",
        },
        unit: {
          ...initialAddEquipmentFormData.unit,
          serial_number: parsed.serial_number || "",
          condition: parsed.condition || "good",
          description: parsed.description || "",
        },
      });

      setScanDialogOpen(false);
      setScanFile(null);
      setEditingEquipment(null);
      setAddDialogOpen(true);
    } catch (err: unknown) {
      let msg = "Failed to analyze the photo. Please try again.";
      if (axios.isAxiosError(err)) {
        msg = err.response?.data?.error?.message || err.message;
      }
      setScanError(msg);
    } finally {
      setIsScanning(false);
    }
  };

  const filteredEquipment = equipmentList.filter((eq) => {
    const catName =
      typeof eq.brand_model?.category === "object"
        ? eq.brand_model?.category?.name
        : eq.brand_model?.category;
    const matchesCategory =
      filterCategory === "all" || catName === filterCategory;
    const matchesStatus = filterStatus === "all" || eq.status === filterStatus;
    const matchesLocation =
      filterLocation === "all" || eq.location === filterLocation;
    return matchesCategory && matchesStatus && matchesLocation;
  });

  const totalPages = Math.ceil(filteredEquipment.length / ROWS_PER_PAGE);
  const paginatedRows = filteredEquipment.slice(
    page * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE + ROWS_PER_PAGE,
  );

  const locations = Array.from(
    new Set(equipmentList.map((e) => e.location || "").filter(Boolean)),
  );

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === paginatedRows.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginatedRows.map((eq) => eq.id)));
    }
  };

  const columns: ColumnDef<IEquipment>[] = [
    {
      key: "select",
      label: "",
      headerRender: () => (
        <Checkbox
          size="small"
          checked={
            paginatedRows.length > 0 &&
            selectedIds.size === paginatedRows.length
          }
          indeterminate={
            selectedIds.size > 0 && selectedIds.size < paginatedRows.length
          }
          onChange={toggleSelectAll}
        />
      ),
      render: (eq) => (
        <Checkbox
          size="small"
          checked={selectedIds.has(eq.id)}
          onChange={() => toggleSelect(eq.id)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      key: "gear_name",
      label: "Name",
      cellSx: { fontWeight: 500, color: theme.palette.text.primary },
      render: (eq) => eq.gear_name || eq.brand_model?.model || "—",
    },
    {
      key: "asset_tag",
      label: "Tag",
      cellSx: { fontWeight: 500, color: theme.palette.text.primary },
      render: (eq) => eq.asset_tag || "—",
    },
    // {
    //   key: "serial_number",
    //   label: "Serial No.",
    //   render: (eq) => (
    //     <Typography
    //       variant="body2"
    //       sx={{ fontFamily: "monospace", fontSize: 12 }}
    //     >
    //       {eq.serial_number || "—"}
    //     </Typography>
    //   ),
    // },
    {
      key: "category",
      label: "Category",
      render: (eq) => {
        const cat = eq.brand_model?.category;
        return typeof cat === "object" ? cat?.name : cat || "—";
      },
    },
    {
      key: "status",
      label: "Status",
      render: (eq) => {
        const cfg = STATUS_CONFIG[eq.status] ?? {
          label: eq.status,
          color: "#9e9e9e",
        };
        return (
          <Chip
            label={cfg.label}
            size="small"
            sx={{
              fontSize: 11,
              height: 22,
              backgroundColor: `${cfg.color}22`,
              color: cfg.color,
              fontWeight: 600,
              border: `1px solid ${cfg.color}44`,
              borderRadius: "16px",
            }}
          />
        );
      },
    },
    {
      key: "unit_index",
      label: "Quantity",
      render: (eq) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily: "monospace",
            fontSize: 12,
            color: theme.palette.text.secondary,
          }}
        >
          {eq.unit_index || "—"}
        </Typography>
      ),
    },
    { key: "location", label: "Location", render: (eq) => eq.location || "—" },

    {
      key: "supplier_name",
      label: "Supplier",
      render: (eq) => eq.supplier_name || "—",
    },
    // {
    //   key: "last_used_date",
    //   label: "Last Used",
    //   render: (eq) => eq.last_used_date || "—",
    // },
    {
      key: "condition",
      label: "Condition",
      render: (eq) => eq.condition || "—",
    },
    {
      key: "action",
      label: "Action",
      align: "center",
      render: (eq) => (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          <Tooltip title="Upload Photo" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenImageUpload(eq);
              }}
            >
              <AddPhotoAlternateOutlined
                sx={{ fontSize: 18, color: theme.palette.text.secondary }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setEditingEquipment(eq);
                setAddDialogOpen(true);
              }}
            >
              <EditOutlined
                sx={{ fontSize: 18, color: theme.palette.text.secondary }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <IconButton
              size="small"
              disabled={deleting === eq.id}
              onClick={(e) => handleDeleteEquipment(eq, e)}
            >
              {deleting === eq.id ? (
                <CircularProgress size={14} />
              ) : (
                <DeleteOutlined sx={{ fontSize: 18, color: "#ef4444" }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const filterSelectSx = {
    fontSize: 14,
    fontWeight: 500,
    height: 40,
    borderRadius: "12px",
    backgroundColor: colors.primary[900],
    color: theme.palette.text.primary,
    minWidth: 140,
    "& .MuiSelect-icon": { color: theme.palette.text.primary },
  };

  return (
    <Box sx={{ p: 2 }}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2 }}>
          <IconButton
            onClick={() => navigate("/gear-management")}
            sx={{ bgcolor: colors.primary[800] }}
          >
            <ArrowBackOutlined />
          </IconButton>
          <Typography
            variant="h5"
            fontWeight="bold"
            color={theme.palette.text.primary}
          >
            All Gear 
          </Typography>
        </Box>
        
        <Typography variant="body2" color={theme.palette.text.secondary} mb={3}>
          Inventory management
        </Typography>

        <Box
          sx={{
            backgroundColor: colors.primary[900],
            borderRadius: "16px",
            p: 4,
            minHeight: "70vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 4,
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {/* Category filter */}
              <FormControl size="small">
                <Select
                  value={filterCategory}
                  onChange={(e) => {
                    setFilterCategory(e.target.value);
                    setPage(0);
                  }}
                  sx={filterSelectSx}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.name}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Status filter */}
              <FormControl size="small">
                <Select
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value);
                    setPage(0);
                  }}
                  sx={filterSelectSx}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
                    <MenuItem key={val} value={val}>
                      {cfg.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Location filter */}
              <FormControl size="small">
                <Select
                  value={filterLocation}
                  onChange={(e) => {
                    setFilterLocation(e.target.value);
                    setPage(0);
                  }}
                  sx={filterSelectSx}
                >
                  <MenuItem value="all">All Locations</MenuItem>
                  {locations.map((l) => (
                    <MenuItem key={l} value={l}>
                      {l}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* View toggle */}
              <Box
                sx={{
                  display: "flex",
                  backgroundColor: colors.primary[900],
                  borderRadius: "10px",
                  p: "4px",
                  border: `1px solid ${colors.primary[400]}`,
                }}
              >
                {(["table", "grid"] as const).map((mode) => (
                  <IconButton
                    key={mode}
                    size="small"
                    onClick={() => setViewMode(mode)}
                    sx={{
                      borderRadius: "8px",
                      backgroundColor:
                        viewMode === mode ? colors.primary[400] : "transparent",
                      color:
                        viewMode === mode
                          ? colors.primary[900]
                          : colors.primary[700],
                      transition: "all 0.2s ease-in-out",
                      px: 1.5,
                    }}
                  >
                    {mode === "table" ? (
                      <ViewListOutlined sx={{ fontSize: 20 }} />
                    ) : (
                      <ViewModuleOutlined sx={{ fontSize: 20 }} />
                    )}
                  </IconButton>
                ))}
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              {selectedIds.size > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => setAddSetDialogOpen(true)}
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    px: 3,
                    height: 40,
                    borderRadius: "10px",
                  }}
                >
                  Create Set ({selectedIds.size})
                </Button>
              )}
              {/* <Button
                variant="contained"
                onClick={() => setAddSetDialogOpen(true)}
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  px: 3,
                  height: 40,
                  borderRadius: "10px",
                }}
              >
                Add Set
              </Button> */}
              <Button
                variant="outlined"
                startIcon={
                  isImporting ? (
                    <CircularProgress size={16} />
                  ) : (
                    <UploadFileOutlined />
                  )
                }
                disabled={isImporting}
                onClick={(e) => setImportExportAnchorEl(e.currentTarget)}
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  px: 3,
                  height: 40,
                  borderRadius: "10px",
                }}
              >
                Import/Export
              </Button>
              <Menu
                anchorEl={importExportAnchorEl}
                open={!!importExportAnchorEl}
                onClose={() => setImportExportAnchorEl(null)}
              >
                <MenuItem onClick={handleExportCSV}>Export to CSV</MenuItem>
                <MenuItem component="label">
                  Import from CSV
                  <input
                    type="file"
                    hidden
                    accept=".csv,text/csv"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImportCSVFile(file);
                      e.target.value = "";
                    }}
                  />
                </MenuItem>
              </Menu>
              <Button
                variant="outlined"
                startIcon={<AutoAwesomeOutlined />}
                onClick={() => {
                  setScanFile(null);
                  setScanError(null);
                  setScanDialogOpen(true);
                }}
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  px: 3,
                  height: 40,
                  borderRadius: "10px",
                }}
              >
                Scan Gear (AI)
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setEditingEquipment(null);
                  setAiPrefillData(null);
                  setAddDialogOpen(true);
                }}
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  px: 3,
                  height: 40,
                  borderRadius: "10px",
                }}
              >
                Add Gear
              </Button>
            </Box>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
              <CircularProgress size={32} sx={{ color: colors.primary[400] }} />
            </Box>
          ) : (
            <AnimatePresence mode="wait">
              {viewMode === "table" ? (
                <motion.div
                  key="table-view"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <AnimatedTable
                    columns={columns}
                    rows={paginatedRows}
                    getRowId={(eq) => eq.id}
                    onRowClick={(eq) =>
                      navigate(`/gear-management/view/${eq.id}`)
                    }
                    page={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    emptyMessage="No equipment found."
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="grid-view"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                  }}
                >
                  {paginatedRows.length === 0 ? (
                    <Box sx={{ py: 8, textAlign: "center" }}>
                      <Typography variant="body1" color="text.secondary">
                        No equipment found.
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(240px, 1fr))",
                        gap: 3,
                        mb: 4,
                      }}
                    >
                      {paginatedRows.map((eq) => {
                        const imageUrl = eq?.images?.[0]?.image;
                        const statusCfg = STATUS_CONFIG[eq.status] ?? {
                          label: eq.status,
                          color: "#9e9e9e",
                        };
                        return (
                          <Card
                            key={eq.id}
                            onClick={() =>
                              navigate(`/gear-management/view/${eq.id}`)
                            }
                            sx={{
                              backgroundColor: colors.primary[800],
                              borderRadius: "14px",
                              backgroundImage: "none",
                              cursor: "pointer",
                              overflow: "hidden",
                              border: `1px solid ${colors.primary[500]}`,
                              height: 260,
                              position: "relative",
                              "&:hover .card-img": { transform: "scale(1.04)" },
                              "&:hover .card-overlay": {
                                transform: "translateY(0)",
                              },
                              "&:hover .card-name-bar": { opacity: 0 },
                            }}
                          >
                            <Box
                              sx={{
                                width: "100%",
                                height: "100%",
                                overflow: "hidden",
                                backgroundColor: colors.primary[900],
                                display: "grid",
                                placeItems: "center",
                              }}
                            >
                              {imageUrl ? (
                                <CardMedia
                                  component="img"
                                  image={imageUrl}
                                  alt={eq.gear_name}
                                  className="card-img"
                                  sx={{
                                    height: "100%",
                                    width: "100%",
                                    objectFit: "contain",
                                    objectPosition: "center",
                                    transition: "transform 0.35s ease",
                                  }}
                                />
                              ) : (
                                <ImageOutlined
                                  sx={{
                                    fontSize: 36,
                                    color: colors.primary[600],
                                  }}
                                />
                              )}

                              <Chip
                                label={statusCfg.label}
                                size="small"
                                sx={{
                                  position: "absolute",
                                  top: 10,
                                  right: 10,
                                  fontSize: "0.7rem",
                                  fontWeight: 600,
                                  height: 20,
                                  backgroundColor: `${statusCfg.color}cc`,
                                  color: "#fff",
                                  zIndex: 2,
                                }}
                              />

                              {/* Select checkbox on card */}
                              <Checkbox
                                size="small"
                                checked={selectedIds.has(eq.id)}
                                onChange={() => toggleSelect(eq.id)}
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                  position: "absolute",
                                  top: 6,
                                  left: 6,
                                  zIndex: 2,
                                  color: "#fff",
                                  "&.Mui-checked": {
                                    color: colors.primary[400],
                                  },
                                }}
                              />
                            </Box>

                            {/* Name bar */}
                            <Box
                              className="card-name-bar"
                              sx={{
                                position: "absolute",
                                bottom: 0,
                                left: 0,
                                right: 0,
                                px: 1.75,
                                pt: 4,
                                pb: 1.25,
                                background:
                                  "linear-gradient(to top, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0) 100%)",
                                opacity: 1,
                                transition: "opacity 0.25s ease",
                                zIndex: 3,
                              }}
                            >
                              <Typography
                                noWrap
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 500,
                                  color: "#fff",
                                }}
                              >
                                {eq.gear_name}
                              </Typography>
                            </Box>

                            <Box
                              className="card-overlay"
                              sx={{
                                position: "absolute",
                                bottom: 0,
                                left: 0,
                                right: 0,
                                px: 1.75,
                                py: 1.5,
                                background: "rgba(15,15,15,0.62)",
                                backdropFilter: "blur(10px)",
                                borderTop: "0.5px solid rgba(255,255,255,0.12)",
                                transform: "translateY(100%)",
                                transition:
                                  "transform 0.32s cubic-bezier(0.25, 0.8, 0.25, 1)",
                                zIndex: 4,
                              }}
                            >
                              <Typography
                                noWrap
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 500,
                                  color: "#fff",
                                  mb: 1,
                                }}
                              >
                                {eq.gear_name}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <LocationOnOutlined
                                    sx={{
                                      fontSize: 13,
                                      color: "rgba(255,255,255,0.6)",
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: 11,
                                      color: "rgba(255,255,255,0.65)",
                                    }}
                                    noWrap
                                  >
                                    {eq.location?.split(",")[0] || "—"}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Inventory2Outlined
                                    sx={{
                                      fontSize: 13,
                                      color: "rgba(255,255,255,0.6)",
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: 11,
                                      fontWeight: 500,
                                      color: "#fff",
                                    }}
                                  >
                                    {eq.serial_number || "—"}
                                  </Typography>
                                </Box>
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: 10,
                                  color: "rgba(255,255,255,0.4)",
                                  mt: 0.75,
                                }}
                              >
                                {typeof eq.brand_model?.category === "object"
                                  ? eq.brand_model?.category?.name
                                  : eq.brand_model?.category || ""}
                              </Typography>
                            </Box>
                          </Card>
                        );
                      })}
                    </Box>
                  )}

                  {totalPages > 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: "auto",
                        gap: 1,
                      }}
                    >
                      {Array.from({ length: totalPages }).map((_, i) => (
                        <Button
                          key={i}
                          onClick={() => setPage(i)}
                          sx={{
                            minWidth: 32,
                            height: 32,
                            p: 0,
                            borderRadius: "8px",
                            backgroundColor:
                              page === i ? colors.primary[400] : "transparent",
                            color:
                              page === i
                                ? colors.primary[900]
                                : theme.palette.text.primary,
                          }}
                        >
                          {i + 1}
                        </Button>
                      ))}
                    </Box>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </Box>

        <AddEquipmentDialog
          open={addDialogOpen}
          onClose={() => {
            setAddDialogOpen(false);
            setEditingEquipment(null);
            setAiPrefillData(null);
          }}
          onSubmit={handleAddEquipment}
          equipment={editingEquipment}
          isEdit={!!editingEquipment}
          initialData={aiPrefillData}
        />

        <AddSetDialog
          open={addSetDialogOpen}
          onClose={() => {
            setAddSetDialogOpen(false);
            setSelectedIds(new Set());
          }}
          onSubmit={async (data) => {
            await apiService.post("/api/equipments/gearsets/", {
              name: data.name,
              description: data.description,
              items: data.items,
            });
            setAddSetDialogOpen(false);
            setSelectedIds(new Set());
          }}
          brandModelList={brandModelList}
          preSelectedIds={[]}
        />

        {/* Image upload dialog */}
        <Dialog
          open={imageUploadOpen}
          onClose={() => !isUploading && setImageUploadOpen(false)}
          PaperProps={{ sx: { borderRadius: 4, minWidth: 400 } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Upload Photo
            </Typography>
            <IconButton
              onClick={() => setImageUploadOpen(false)}
              disabled={isUploading}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 3, pb: 4 }}
          >
            <Typography variant="body2" color="text.secondary">
              Attach a photo for{" "}
              <strong>
                {equipmentForImage?.gear_name ||
                  equipmentForImage?.brand_model?.model}
              </strong>
              .
            </Typography>
            <Button
              variant="outlined"
              component="label"
              sx={{
                py: 4,
                borderStyle: "dashed",
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <AddPhotoAlternateOutlined
                sx={{ fontSize: 40, color: "text.secondary" }}
              />
              <Typography>
                {selectedFile ? selectedFile.name : "Click to select image"}
              </Typography>
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files?.[0]) setSelectedFile(e.target.files[0]);
                }}
              />
            </Button>
            <Button
              variant="contained"
              onClick={handleUploadImageSubmit}
              disabled={!selectedFile || isUploading}
              sx={{
                backgroundColor: colors.primary[400],
                borderRadius: "24px",
                py: 1.2,
              }}
            >
              {isUploading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Upload Image"
              )}
            </Button>
          </DialogContent>
        </Dialog>

        {/* Scan gear from photo (AI) dialog */}
        <Dialog
          open={scanDialogOpen}
          onClose={() => !isScanning && setScanDialogOpen(false)}
          PaperProps={{ sx: { borderRadius: 4, minWidth: 400 } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Scan Gear Photo
            </Typography>
            <IconButton
              onClick={() => setScanDialogOpen(false)}
              disabled={isScanning}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 3, pb: 4 }}
          >
            <Typography variant="body2" color="text.secondary">
              Upload a photo of the gear and AI will try to prefill the add-gear
              form. You&apos;ll still review and confirm before saving.
            </Typography>
            <Button
              variant="outlined"
              component="label"
              sx={{
                py: 4,
                borderStyle: "dashed",
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <AddPhotoAlternateOutlined
                sx={{ fontSize: 40, color: "text.secondary" }}
              />
              <Typography>
                {scanFile ? scanFile.name : "Click to select image"}
              </Typography>
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files?.[0]) setScanFile(e.target.files[0]);
                }}
              />
            </Button>
            {scanError && <Alert severity="error">{scanError}</Alert>}
            <Button
              variant="contained"
              onClick={handleScanSubmit}
              disabled={!scanFile || isScanning}
              sx={{
                backgroundColor: colors.primary[400],
                borderRadius: "24px",
                py: 1.2,
              }}
            >
              {isScanning ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Analyze Photo"
              )}
            </Button>
          </DialogContent>
        </Dialog>
      </motion.div>
    </Box>
  );
};

export default ListGear;
